import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPathologistReports,
  fetchPathologistReportById,
  deletePathologistReport,
} from "../../store/slices/pathologistResultSlice";
import PathologistReportView from "./PathologistReportView";
import PathologistReportForm from "./PathologistReportForm";

const PathologistReportsList = () => {
  const dispatch = useDispatch();
  const { reports, loading, error } = useSelector(
    (s) => s.pathologistReports || {}
  );
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);

  useEffect(() => {
    dispatch(fetchPathologistReports());
  }, [dispatch]);

  const openView = async (r) => {
    setSelected(r);
    await dispatch(fetchPathologistReportById(r.id)).unwrap();
    setShowView(true);
  };

  const openForm = (r = null) => {
    setSelected(r);
    setShowForm(true);
  };

  const handleDelete = (r) => {
    if (!window.confirm("Delete this report?")) return;
    dispatch(deletePathologistReport(r.id));
  };

  const primaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a4a5a] focus:outline-none focus:ring-2 focus:ring-[#235F72] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const secondaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#235F72] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const linkBase = "text-sm font-semibold hover:underline";

  return (
    <div className="mx-auto w-full max-w-6xl p-4 sm:p-6">
      <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
        <div className="border-b border-gray-200/70 bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#235F72]/10 px-3 py-1 text-xs font-semibold text-[#235F72] ring-1 ring-[#235F72]/20">
                Pathology
              </div>
              <h2 className="mt-2 text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
                Pathologist Reports
              </h2>
              <p className="mt-1 text-sm text-gray-600">Browse, edit, and verify reports.</p>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => dispatch(fetchPathologistReports())} className={secondaryBtn} disabled={loading}>
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400/80 border-t-transparent" />
                    Refreshing…
                  </>
                ) : (
                  "Refresh"
                )}
              </button>
              <button onClick={() => openForm(null)} className={primaryBtn}>
                New Report
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="border-b border-gray-200/70 bg-red-50 px-5 py-4 text-sm text-red-800 sm:px-6">
            {String(error)}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-4 py-3 sm:px-6">#</th>
                <th className="px-4 py-3 sm:px-6">Patient</th>
                <th className="px-4 py-3 sm:px-6">Test</th>
                <th className="px-4 py-3 sm:px-6">Pathologist</th>
                <th className="px-4 py-3 sm:px-6">Status</th>
                <th className="px-4 py-3 sm:px-6">Created</th>
                <th className="px-4 py-3 sm:px-6">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(Array.isArray(reports) ? reports : []).map((r) => (
                <tr key={r.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3 font-semibold text-gray-900 sm:px-6">#{r.id}</td>
                  <td className="px-4 py-3 text-gray-800 sm:px-6">{r.patient_name || "-"}</td>
                  <td className="px-4 py-3 text-gray-800 sm:px-6">{r.test_name || "-"}</td>
                  <td className="px-4 py-3 text-gray-700 sm:px-6">{r.pathologist_name || "-"}</td>
                  <td className="px-4 py-3 sm:px-6">
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                      {String(r.status || "-")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 sm:px-6">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "-"}
                  </td>
                  <td className="px-4 py-3 sm:px-6">
                    <div className="flex flex-wrap gap-3">
                      <button onClick={() => openView(r)} className={`${linkBase} text-[#235F72]`}>
                        View
                      </button>
                      <button onClick={() => openForm(r)} className={`${linkBase} text-green-700`}>
                        Edit
                      </button>
                      <button onClick={() => handleDelete(r)} className={`${linkBase} text-red-700`}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(!reports || reports.length === 0) && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500 sm:px-6">
                    {loading ? "Loading reports…" : "No reports found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-gray-200/70">
            <div className="flex items-start justify-between gap-3 border-b border-gray-200/70 bg-gray-50 px-5 py-4">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  {selected ? "Edit Report" : "New Report"}
                </div>
                <div className="text-xs text-gray-500">Complete results and save.</div>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="rounded-lg px-2 py-1 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <PathologistReportForm
              report={selected}
              onClose={() => {
                setShowForm(false);
                dispatch(fetchPathologistReports());
              }}
            />
          </div>
        </div>
      )}

      {showView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-gray-200/70">
            <div className="flex items-start justify-between gap-3 border-b border-gray-200/70 bg-gray-50 px-5 py-4">
              <div>
                <div className="text-sm font-semibold text-gray-900">Report #{selected?.id || ""}</div>
                <div className="text-xs text-gray-500">Read-only view.</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => window.print()} className={primaryBtn}>
                  Print
                </button>
                <button
                  onClick={() => setShowView(false)}
                  className="rounded-lg px-2 py-1 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="max-h-[80vh] overflow-y-auto overscroll-contain">
              <PathologistReportView />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PathologistReportsList;
